<template>
  <div class="game-act-manager">
    <!-- 導航選單 -->
    <div class="nav-menu" v-show="showNavMenu">
      <div class="nav-title">{{ profile.name }}</div>
      <div class="nav-usage" v-if="activityUsage.loaded">
        {{ t('gameActManager.activityUsage') }}: {{ activityUsage.currentCount }} / {{ activityUsage.maxLimit }}
      </div>
      <div class="nav-item" @click="logout">登出</div>
    </div>

    <!-- 活動操作選單 -->
    <div class="actions" v-show="showActionMenu"
      :style="{ left: actionMenuPos.x + 'px', top: actionMenuPos.y + 'px' }">
      <div class="action-item" @click="openSettingModal()">{{ t('gameActManager.settings') }}</div>
      <div class="action-item" @click="duplicateActivity()">{{ t('gameActManager.duplicate') }}</div>
      <div class="action-item" @click="confirmDeleteActivity()">{{ t('gameActManager.deleteAction') }}</div>
    </div>


    <!-- 中小螢幕搜尋框（全螢幕） -->
    <div class="search-box" v-show="showSearchBox">
      <div class="search-wrap">
        <div class="back-btn" @click="closeSearchBox()">
          <svg-icon icon-class="arrow-pre" />
        </div>

        <label class="search-bar">
          <input :placeholder="t('gameActManager.searchPlaceholder')" v-model="search" />
          <div class="delete-btn" v-show="search.length !== 0" @click="resetSearch()">
            <svg-icon icon-class="close" />
          </div>
          <button type="submit" class="search-btn" @click="searchGameActs()" :disabled="search.length == 0">
            <svg-icon icon-class="search" class="search-icon" />
          </button>
        </label>
      </div>
    </div>

    <!-- 頂部 Navbar -->
    <div class="flex-bar">
      <div class="left">
        <div class="title">
          <span class="title-text">{{ t('gameActManager.pageTitle') }}</span>
        </div>
      </div>
      <div class="right" v-if="!isLogin">
        <div @click="loginTMID()" class="login-btn">登入</div>
      </div>
      <div class="right" v-if="isLogin">
        <!--搜尋（桌面版）-->
        <div class="search-wrap">
          <label class="search-bar">
            <input :placeholder="t('gameActManager.searchPlaceholder')" v-model="search" />
            <div class="delete-btn" v-show="search.length !== 0" @click="resetSearch()">
              <svg-icon icon-class="close" />
            </div>
            <button type="submit" class="search-btn" @click="searchGameActs()" :disabled="search.length == 0">
              <svg-icon icon-class="search" class="search-icon" />
            </button>
          </label>
        </div>
        <!--搜尋End-->

        <!--中螢幕搜尋按鈕-->
        <div class="view-btn search-btn-mid" @click="openSearchBox()">
          <svg-icon icon-class="search" class="search-icon" />
        </div>

        <div class="avatar-wrap" @click="OpenAvatarMenu()">
          <img class="avatar" :src="profile.picture" alt="">
        </div>
      </div>
    </div>

    <!--SideBar-->
    <div class="sidebar-container" v-show="isLogin">
      <div class="sidebar">
        <!--新增按鈕-->
        <div class="add-btn" @click="handleAddButtonClick" ref="addButtonRef">+ {{ t('gameActManager.addNew') }}</div>

        <!-- 步驟一引導：當遊戲活動為空時顯示 -->
        <GuideStep1 v-if="gameActivityList.length === 0" ref="guideStep1Ref" />

        <div class="sidebar-content">
          <div class="sidebar-item active">{{ t('gameActManager.myActivities') }}</div>
        </div>
      </div>

      <div class="content">
        <!--gridView-->
        <div class="activity-grid">
          <div class="activity-card-wrap" v-if="gameActivityList.length > 0">
            <div class="card-item" v-for="activity in gameActivityList" :key="activity.id">
              <div class="img-top" @click="clickActivityItem(activity)" :style="{ backgroundImage: `url(${getActivityBackgroundImage(activity)})` }">
                <!-- 背景圖片 -->
              </div>

              <div class="card-textwrap">
                <h5 @click="clickActivityItem(activity)">{{ activity.activityName }}</h5>
                <p class="update-time">{{ getTime(activity.updateTime) }}</p>
              </div>

              <!-- 左下角綠燈指示器（開放進入活動時顯示） -->
              <div class="status-indicator" v-if="activity.enableEntry">
                <span class="status-dot"></span>
                <span class="status-text">{{ t('gameActManager.statusOpen') }}</span>
              </div>

              <div class="more-btn" @click="openActionMenu(activity, $event)">
                <svg-icon icon-class="more" />
              </div>
              <p class="share" v-show="activity.isPublished">
                <svg-icon iconClass="eye"></svg-icon>
                <span class="publish-note-text">已發布</span>
              </p>
            </div>
          </div>
          <div class="text-md ml-8 mt-8 font-bold" v-else>
            {{ t('gameActManager.noActivities') }}
          </div>
        </div>
      </div>
    </div>

    <!-- 分頁 - fixed position -->
    <div class="page-btn-wrap" v-if="isLogin && gameActivityList.length > 0">
      <div v-for="pageBtn in pageBtns" :key="pageBtn" class="page-btn"
        :class="{ 'page-btn-active': pageBtn == currentPageNum }"
        @click="getActivityListByPage(pageBtn)">
        {{ pageBtn }}
      </div>
    </div>

    <!-- 新增活動 Modal -->
    <CreateActivityModal
      v-if="showCreateModal"
      :question-type="selectedQuestionType"
      :creator="{ id: profile.teammodelId, name: profile.name }"
      :default-settings="defaultGameSettings"
      :activity-count="gameActivityList.length"
      @close="handleModalClose"
      @created="handleActivityCreated"
    />

    <!-- 更新活動 Modal -->
    <UpdateActivityModal
      v-if="showUpdateModal"
      :activity="currentActivity"
      :creator="{ id: profile.teammodelId, name: profile.name }"
      :activity-count="gameActivityList.length"
      @close="handleUpdateModalClose"
      @updated="handleActivityUpdated"
    />

    <!-- 刪除確認視窗 -->
    <div v-show="showDeleteConfirm" class="modal-overlay">
      <div class="modal-content delete-confirm-modal">
        <div class="modal-header">
          <h3>{{ t('gameActManager.deleteConfirmTitle') }}</h3>
          <button class="close-btn" @click="closeDeleteConfirm">
            <svg-icon icon-class="close" />
          </button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">{{ t('gameActManager.deleteConfirmMessage') }}</p>
          <p class="warning-text danger-color">{{ activityToDelete?.activityName }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="cancel-btn" @click="closeDeleteConfirm" :disabled="isDeleting">
            {{ t('gameActManager.cancel') }}
          </button>
          <button type="button" class="delete-btn" @click="confirmDelete" :disabled="isDeleting">
            <span v-if="isDeleting" class="loading-spinner"></span>
            {{ isDeleting ? t('gameActManager.deleting') : t('gameActManager.delete') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 遊戲選擇彈出視窗 -->
    <div class="popup-overlay" v-if="showGameSelectionModal" @click="closeGameSelectionModal"></div>
    <div class="game-selection-popup" v-if="showGameSelectionModal">
      <div class="popup-header">
        <h2>{{ t('gameActManager.selectGameType') }}</h2>
        <div class="close-btn" @click="closeGameSelectionModal">
          <svg-icon icon-class="close" />
        </div>
      </div>
      <div class="popup-content">
        <div class="game-grid">
          <div class="game-item" @click="createGameActivity('mole', 'recognition')">
            <div class="game-card">
              <img :src="require('@/assets/HTwbActAssets/home/cover-molegame.webp')" :alt="t('gameActManager.moleGameCard')">
              <div class="game-name">{{ t('gameActManager.moleGameCard') }}</div>
            </div>
          </div>
          <div class="game-item" @click="createGameActivity('star', 'recognition')">
            <div class="game-card">
              <img :src="require('@/assets/HTwbActAssets/home/cover-pvpgame.webp')" :alt="t('gameActManager.starGameCard')">
              <div class="game-name">{{ t('gameActManager.starGameCard') }}</div>
            </div>
          </div>
          <div class="game-item" @click="createGameActivity('tf', 'trueFalse')">
            <div class="game-card">
              <img :src="require('@/assets/HTwbActAssets/home/cover-tfgame.webp')" :alt="t('gameActManager.rocketGameCard')">
              <div class="game-name">{{ t('gameActManager.rocketGameCard') }}</div>
            </div>
          </div>
          <div class="game-item" @click="createGameActivity('snake', 'recognition')">
            <div class="game-card">
              <img :src="require('@/assets/HTwbActAssets/home/cover-snakegame.webp')" :alt="t('gameActManager.snakeGameCard')">
              <div class="game-name">{{ t('gameActManager.snakeGameCard') }}</div>
            </div>
          </div>
          <div class="game-item" @click="createGameActivity('maze', 'multiChoice')">
            <div class="game-card">
              <img :src="require('@/assets/HTwbActAssets/home/cover-mazegame.webp')" :alt="t('gameActManager.mazeGameCard')">
              <div class="game-name">{{ t('gameActManager.mazeGameCard') }}</div>
            </div>
          </div>
          <div class="game-item" @click="createGameActivity('aigame', 'aiGenerated')">
            <div class="game-card">
              <img :src="require('@/assets/HTwbActAssets/home/cover-aigame.webp')" :alt="t('gameActManager.aiGameCard')">
              <div class="game-name">{{ t('gameActManager.aiGameCard') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/*eslint-disable*/
import { ref, reactive, onMounted, onBeforeUnmount, inject, toRaw } from 'vue';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import jwt_decode from "jwt-decode";
import SvgIcon from "../components/SvgIcon.vue";
import CreateActivityModal from "./components/CreateActivityModal.vue";
import UpdateActivityModal from "./components/UpdateActivityModal.vue";
import GuideStep1 from "./components/GuideStep1.vue";
import { useMainStore } from "../stores/mainStore";
import api from "@/api";
import gameActivityAPI from "@/api/gameActivityAPI";

const store = useMainStore();
const $q = useQuasar();
const router = useRouter();
const $Message = inject('$Message');
const { t } = useI18n();

// 使用者資料
const profile = reactive({
  name: '',
  picture: '',
  teammodelId: ''
});

// UI 狀態
const isLogin = ref(false);
const showNavMenu = ref(false);
const showActionMenu = ref(false);
const showAddMenu = ref(false);
const showSearchBox = ref(false);
const showGameSelectionModal = ref(false);
const search = ref('');
const currentPageNum = ref(1);
const totalPages = ref(1);

// 活動用量狀態
const activityUsage = reactive({
  loaded: false,
  currentCount: 0,
  maxLimit: 120,
  remainingCount: 0,
  canCreate: true,
  isNearLimit: false
});

// 遊戲活動列表
const gameActivityList = ref([]);
const currentActivity = ref(null);

// 刪除確認相關
const showDeleteConfirm = ref(false);
const activityToDelete = ref(null);
const isDeleting = ref(false);

// 分頁按鈕
const pageBtns = ref([1]);

// 選單位置
const actionMenuPos = reactive({
  x: 0,
  y: 0
});

// 初始化認證
const initializeAuth = async () => {
    try {
        const res = await api.main.getTokenUnlogin();
        const exp = jwt_decode(res.auth_token).exp
        localStorage.setItem("auth_token", res.auth_token);
        localStorage.setItem("auth_token_expiry", exp);
    } catch (error) {
        console.error("Error fetching auth token ", error);
    }
}

// 初始化
onMounted(async () => {
    try {
        await initializeAuth();
    } catch (error) {
        console.error("Error fetching auth token:", error);
    }

    document.addEventListener('click', handleNavClickOutside);
    document.addEventListener('click', handleActionMenuClickOutside);
    document.addEventListener('click', handleAddMenuClickOutside);

    // 優先檢查 URL 中是否有 code 參數
    let url = new URL(window.location.href);
    let code = url.searchParams.has("code") == true ? url.searchParams.get("code") : "";

    if (code) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("expires_in");
        localStorage.removeItem("profile");
        //登入取得token
        api.main
            .getToken()
            .then(async (res) => {
                localStorage.setItem("id_token", res.id_token);
                const decoded = jwt_decode(res.id_token);

                isLogin.value = true;
                store.setIsLogIn(true);
                profile.name = decoded.name;
                profile.teammodelId = decoded.sub;
                profile.picture = decoded.picture;

                //將access_token存本地, http 攔截器使用
                localStorage.setItem("access_token", res.access_token);
                localStorage.setItem("expires_in", res.expires_in);

                localStorage.setItem(
                    "profile",
                    JSON.stringify({
                        name: profile.name,
                        id: profile.teammodelId,
                        picture: profile.picture,
                    })
                );

                $q.loading.show();
                await loadGameActivities();
                $q.loading.hide();

                const url = new URL(window.location.href);
                url.searchParams.delete('code');
                url.searchParams.delete('state');
                url.searchParams.delete('clientId');
                window.history.replaceState({}, document.title, url.toString());
            })
            .catch((err) => {
                console.log(err);
            });
    }
    // 檢查是否已有登入資料（包含從 HT 跳轉或一般登入）
    else if (localStorage.getItem("profile") && localStorage.getItem("access_token")) {
        isLogin.value = true;
        let localeProfile = JSON.parse(localStorage.getItem("profile"));
        profile.name = localeProfile.name;
        profile.teammodelId = localeProfile.id || localeProfile.teammodelId;
        profile.picture = localeProfile.picture;

        try {
            $q.loading.show();
            await loadGameActivities();
            $q.loading.hide();
        } catch (error) {
            $q.loading.hide();
            console.error('載入遊戲活動失敗:', error);
            // 如果是 401 錯誤，response interceptor 會自動處理
            // 這裡只需要確保 loading 被關閉
        }
    }
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleNavClickOutside);
    document.removeEventListener('click', handleActionMenuClickOutside);
    document.removeEventListener('click', handleAddMenuClickOutside);
});

// 載入活動用量資訊
const loadActivityUsage = async () => {
  try {
    const request = {
      tmid: profile.teammodelId
    };

    const res = await gameActivityAPI.checkActivityLimit(request);
    console.log('[GameActManager] 用量查詢結果:', res);

    if (res && res.data) {
      activityUsage.currentCount = res.data.currentCount || 0;
      activityUsage.maxLimit = res.data.maxLimit || 120;
      activityUsage.remainingCount = res.data.remainingCount || 0;
      activityUsage.canCreate = res.data.canCreate !== false;
      activityUsage.isNearLimit = res.data.isNearLimit || false;
      activityUsage.loaded = true;
    }
  } catch (err) {
    console.error('載入活動用量失敗:', err);
    activityUsage.loaded = false;
  }
};

// 載入遊戲活動列表（改為呼叫後端 API）
const loadGameActivities = async () => {
  try {
    const request = {
      tmid: profile.teammodelId, // 必填：登入者 ID
      sortOrder: 'desc',
      sortField: 'createTime',
      pageSize: 12, // 與前端每頁顯示數一致
      pageNumber: currentPageNum.value,
      searchText: (search.value || '').trim()
    };

    const res = await gameActivityAPI.getMyGameActivities(request);
    // 後端回傳 { status, data, totalPages }
    gameActivityList.value = Array.isArray(res.data) ? res.data : [];
    totalPages.value = res.totalPages || 1;
    updatePagination();

    // 載入活動列表後，同時載入用量資訊
    await loadActivityUsage();
  } catch (err) {
    console.error('載入遊戲活動失敗:', err);
    gameActivityList.value = [];
    totalPages.value = 1;
    updatePagination();
  }
};

// 更新分頁
const updatePagination = () => {
  const pages = [];
  for (let i = 1; i <= totalPages.value; i++) {
    pages.push(i);
  }
  pageBtns.value = pages;
};

// 搜尋
const searchGameActs = async () => {
  currentPageNum.value = 1;
  await loadGameActivities();
};

const resetSearch = () => {
  search.value = '';
  searchGameActs();
};

// 開啟搜尋框（中小螢幕）
const openSearchBox = () => {
  showSearchBox.value = true;
};

// 關閉搜尋框（中小螢幕）
const closeSearchBox = () => {
  showSearchBox.value = false;
};

// 分頁切換
const getActivityListByPage = async (pageNum) => {
  currentPageNum.value = pageNum;
  await loadGameActivities();
};

// 點擊活動卡片 - 直接開啟設定 Modal
const clickActivityItem = (activity) => {
  console.log('點擊活動:', toRaw(activity));
  currentActivity.value = activity;
  showUpdateModal.value = true;
};

// 開啟操作選單
const openActionMenu = (activity, event) => {
  event.stopPropagation();
  currentActivity.value = activity;

  // 計算選單位置
  const rect = event.target.getBoundingClientRect();
  actionMenuPos.x = rect.left - 60;
  actionMenuPos.y = rect.top + 20;

  showActionMenu.value = true;
};

// 點擊外部關閉操作選單
const handleActionMenuClickOutside = (event) => {
  if (!event.target.closest('.actions') && !event.target.closest('.more-btn')) {
    showActionMenu.value = false;
  }
};

// 開啟設定 Modal（更新活動）
const openSettingModal = () => {
  showActionMenu.value = false;
  if (currentActivity.value) {
    showUpdateModal.value = true;
  }
};

// 開啟刪除確認視窗
const confirmDeleteActivity = () => {
  showActionMenu.value = false;
  if (currentActivity.value) {
    activityToDelete.value = currentActivity.value;
    showDeleteConfirm.value = true;
  }
};

// 關閉刪除確認視窗
const closeDeleteConfirm = () => {
  showDeleteConfirm.value = false;
  activityToDelete.value = null;
};

// 確認刪除活動
const confirmDelete = async () => {
  if (!activityToDelete.value || isDeleting.value) return;

  isDeleting.value = true;

  try {
    console.log('開始刪除活動:', activityToDelete.value);

    // 呼叫刪除 API
    const response = await gameActivityAPI.deleteGameActivity({
      id: activityToDelete.value.id
    });

    console.log('刪除 API 回應:', response);

    if (response && response.status === 200) {
      $Message.success(t('gameActManager.activityDeleted'));
      closeDeleteConfirm();

      // 重新載入活動列表
      $q.loading.show();
      await loadGameActivities();
      $q.loading.hide();
    } else {
      throw new Error('刪除請求失敗');
    }
  } catch (error) {
    console.error('刪除活動失敗:', error);
    $Message.error(error.message || t('gameActManager.activityDeleteFailed'));
  } finally {
    isDeleting.value = false;
  }
};

// 複製活動
const duplicateActivity = async () => {
  showActionMenu.value = false;
  if (!currentActivity.value) return;

  try {
    $q.loading.show();

    // 建立複製的活動資料（排除成績和邀請碼相關欄位）
    const originalActivity = currentActivity.value;
    const newActivityName = `${originalActivity.activityName}${t('gameActManager.copySuffix')}`;

    // 解析並更新遊戲內容設定中的 topic 欄位
    let gameContentSettings = originalActivity.gameContentSettings || '{}';
    try {
      const settings = JSON.parse(gameContentSettings);
      settings.topic = newActivityName; // 更新主題為新的活動名稱
      gameContentSettings = JSON.stringify(settings);
    } catch (error) {
      console.error('解析遊戲設定失敗，使用原始設定:', error);
    }

    // 確保必要欄位存在
    const duplicatedData = {
      activityName: newActivityName,
      creator: {
        id: profile.teammodelId,
        name: profile.name
      },
      currentGame: originalActivity.currentGame,
      questionType: originalActivity.questionType,
      gameContentSettings: gameContentSettings,
      classRosterSource: originalActivity.classRosterSource,
      classRoster: JSON.stringify({ students: [] }), // 清空班級名單
      shortUrlCode: '', // 清空短網址
      enableEntry: false, // 重置為未開放
      playerCount: originalActivity.playerCount,
      allowTemplateChange: originalActivity.allowTemplateChange
    };

    console.log('複製活動資料:', duplicatedData);

    // 呼叫 API 建立新活動
    const response = await gameActivityAPI.createGameActivity(duplicatedData);

    console.log('複製活動成功:', response);
    $Message.success(t('gameActManager.activityDuplicated'));

    // 重新載入活動列表
    await loadGameActivities();
    $q.loading.hide();

    // 開啟新複製的活動設定視窗
    try {
      const createdActivity = (response && response.data) ? response.data : response;
      let target = null;

      if (createdActivity && createdActivity.id) {
        target = gameActivityList.value.find(a => a.id === createdActivity.id) || null;
      }

      // 若無法由回傳值判定，取列表第一筆（列表以 createTime desc 排序，最新在前）
      if (!target && Array.isArray(gameActivityList.value) && gameActivityList.value.length > 0) {
        target = gameActivityList.value[0];
      }

      if (target) {
        currentActivity.value = target;
        showUpdateModal.value = true;
      }
    } catch (e) {
      console.error('開啟更新視窗時發生錯誤:', e);
    }
  } catch (error) {
    console.error('複製活動失敗:', error);

    // 檢查是否為活動數量超限錯誤
    const isLimitExceeded = error.response?.data?.errorCode === 'ACTIVITY_LIMIT_EXCEEDED' ||
                           error.response?.data?.message?.includes('已達活動數量上限') ||
                           error.response?.data?.message?.includes('已达活动数量上限');

    if (isLimitExceeded) {
      const maxLimit = error.response?.data?.maxLimit || 120;
      $Message.warning({
        content: t('gameActManager.activityLimitExceeded', { maxLimit }),
        duration: 6,
        closable: true
      });
    } else {
      $Message.error(error.message || t('gameActManager.activityDuplicateFailed'));
    }

    $q.loading.hide();
  }
};

// 切換遊戲選擇彈窗
const toggleGameSelectionModal = () => {
  showGameSelectionModal.value = true;
};

// 關閉遊戲選擇彈窗
const closeGameSelectionModal = () => {
  showGameSelectionModal.value = false;
};

// 切換新增選單（保留，以防其他地方使用）
const toggleAddMenu = () => {
  showAddMenu.value = !showAddMenu.value;
};

// 點擊外部關閉新增選單
const handleAddMenuClickOutside = (event) => {
  if (!event.target.closest('.add-menu') && !event.target.closest('.add-btn')) {
    showAddMenu.value = false;
  }
};

// 建立活動相關狀態
const showCreateModal = ref(false);
const selectedQuestionType = ref('');
// 建立活動：遊戲詳細內容設定預設值
const getDefaultGameSettings = (gameType, questionType) => {
  return {
    playerCount: 1,
    currentGame: gameType,
    allowTemplateChange: false,
    gameParameters: {}
  };
};
const defaultGameSettings = ref(getDefaultGameSettings('mole', 'recognition'));

// 更新活動相關狀態
const showUpdateModal = ref(false);

// 引導組件 ref
const guideStep1Ref = ref(null);
const addButtonRef = ref(null);

// 處理「+ 新增」按鈕點擊
const handleAddButtonClick = () => {
  // 隱藏步驟一引導
  if (guideStep1Ref.value) {
    guideStep1Ref.value.hideGuide();
  }
  // 開啟遊戲選擇彈窗
  toggleGameSelectionModal();
};

// 建立遊戲活動（新方法：指定遊戲類型和題型）
const createGameActivity = (gameType, questionType) => {
  closeGameSelectionModal();
  selectedQuestionType.value = questionType;
  // 設定預設遊戲設定
  defaultGameSettings.value = getDefaultGameSettings(gameType, questionType);
  showCreateModal.value = true;
};

// 建立活動（保留舊方法，以防其他地方使用）
const createActivity = (type) => {
  showAddMenu.value = false;
  selectedQuestionType.value = type;
  // 每次開啟前重置預設設定（根據題型）
  defaultGameSettings.value = getDefaultGameSettings('mole', type);
  showCreateModal.value = true;
};

// Modal 關閉處理
const handleModalClose = () => {
  showCreateModal.value = false;
  selectedQuestionType.value = '';
};

// 活動建立成功處理
const handleActivityCreated = async (res) => {
  // 關閉新增視窗
  showCreateModal.value = false;
  selectedQuestionType.value = '';
  $Message.success(t('gameActManager.activityCreated'));

  // 重新載入活動列表
  $q.loading.show();
  await loadGameActivities();
  $q.loading.hide();

  // 嘗試取得剛建立的活動並開啟更新視窗
  try {
    // 後端可能回傳 { status, data } 或直接回傳活動物件
    const createdActivity = (res && res.data) ? res.data : res;
    let target = null;

    if (createdActivity && createdActivity.id) {
      target = gameActivityList.value.find(a => a.id === createdActivity.id) || null;
    }

    // 若無法由回傳值判定，取列表第一筆（列表以 createTime desc 排序，最新在前）
    if (!target && Array.isArray(gameActivityList.value) && gameActivityList.value.length > 0) {
      target = gameActivityList.value[0];
    }

    if (target) {
      currentActivity.value = target;
      showUpdateModal.value = true;
    }
  } catch (e) {
    console.error('開啟更新視窗時發生錯誤:', e);
  }
};

// 更新 Modal 關閉處理
const handleUpdateModalClose = () => {
  showUpdateModal.value = false;
};

// 活動更新成功處理
const handleActivityUpdated = async () => {
  showUpdateModal.value = false;
  $Message.success(t('gameActManager.activityUpdated'));

  // 重新載入活動列表
  $q.loading.show();
  await loadGameActivities();
  $q.loading.hide();
};

// Avatar 選單
const OpenAvatarMenu = () => {
  showNavMenu.value = !showNavMenu.value;
};

const handleNavClickOutside = (event) => {
    if (!event.target.closest('.nav-menu') && !event.target.closest('.avatar')) {
        showNavMenu.value = false;
    }
}

// 登出
const logout = () => {
    showNavMenu.value = false;
    if (isLogin.value) {
        // 檢查是否在 WebView 環境
        const isWebView = window?.chrome?.webview ? true : false;

        api.main.logout().then(async () => {
            $Message.info("已登出");
            isLogin.value = false;
            store.setIsLogIn(false);
            localStorage.clear();
            sessionStorage.clear();

            // 如果在 WebView 環境，導航回 HTwbActKitHome 並重整畫面
            if (isWebView) {
                window.location.href = '/htwb-actkit-home';
            }
        });
    }
}

// 登入
const loginTMID = () => {
    api.main.loginTmdID();
};

// 格式化時間（支援秒與毫秒時間戳）
const getTime = (timestamp) => {
  if (timestamp === null || timestamp === undefined || timestamp === '') return '';
  const n = Number(timestamp);
  if (Number.isNaN(n)) return '';
  // 小於 1e12 視為秒，需要轉毫秒
  const ms = n < 1e12 ? n * 1000 : n;
  const date = new Date(ms);
  return date.toLocaleDateString('zh-TW');
};

// 取得活動背景圖
const getActivityBackgroundImage = (activity) => {
  try {
    // 解析 gameContentSettings
    const settings = activity.gameContentSettings
      ? JSON.parse(activity.gameContentSettings)
      : {};

    const background = settings.background || 'forest';
    const currentGame = activity.currentGame || 'mole';

    // 根據遊戲類型和背景設定返回對應圖片
    if (currentGame === 'mole') {
      // 打地鼠遊戲
      if (background === 'forest') {
        return require('@/assets/HTwbActAssets/MolegameObj/mole-forest.webp');
      } else if (background === 'desert') {
        return require('@/assets/HTwbActAssets/MolegameObj/mole-desert.webp');
      }
    } else if (currentGame === 'star') {
      // 摘星星遊戲 - 統一使用 cover-pvpgame.webp
      return require('@/assets/HTwbActAssets/home/cover-pvpgame.webp');
    } else if (currentGame === 'tf') {
      // 火箭升空遊戲（是非題）
      return require('@/assets/HTwbActAssets/home/cover-tfgame.webp');
    } else if (currentGame === 'snake') {
      // 貪食蛇遊戲
      return require('@/assets/HTwbActAssets/home/cover-snakegame.webp');
    } else if (currentGame === 'maze') {
      // 迷宮遊戲（單選題）
      return require('@/assets/HTwbActAssets/home/cover-mazegame.webp'); // 暫時使用貪食蛇圖片，待確認實際圖片路徑
    } else if (currentGame === 'aigame') {
      // 自製遊戲
      return require('@/assets/HTwbActAssets/home/cover-aigame.webp');
    }

    // 預設圖片
    return require('@/assets/HTwbActAssets/MolegameObj/mole-forest.webp');
  } catch (error) {
    console.error('解析活動背景圖失敗:', error);
    return require('@/assets/HTwbActAssets/MolegameObj/mole-forest.webp');
  }
};
</script>

<style lang="less">
@import "../assets/color.less";

.game-act-manager {
    .select-btn {
        &:focus {
            --tw-ring-color: @light-color !important;
        }

        .arrow-icon-up {
            color: @light-color !important;
        }
    }
}
</style>

<style lang="less" scoped>
@import "../assets/gameActManager.less";
</style>
